import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
	fetchOrderStatusSummary,
	fetchAssignmentSummary,
	fetchPaymentSummary,
} from "../../pages/dashboard/handlers/dashboard.handlers";

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const loadOrderSummary = createAsyncThunk(
	"dashboard/loadOrderSummary",
	async (_, { rejectWithValue }) => {
		try {
			return await fetchOrderStatusSummary();
		} catch (error) {
			return rejectWithValue(error.message);
		}
	},
);

export const loadAssignmentSummary = createAsyncThunk(
	"dashboard/loadAssignmentSummary",
	async (_, { rejectWithValue }) => {
		try {
			return await fetchAssignmentSummary();
		} catch (error) {
			return rejectWithValue(error.message);
		}
	},
);

export const loadPaymentSummary = createAsyncThunk(
	"dashboard/loadPaymentSummary",
	async (_, { rejectWithValue }) => {
		try {
			return await fetchPaymentSummary();
		} catch (error) {
			return rejectWithValue(error.message);
		}
	},
);

// ─── Initial State ────────────────────────────────────────────────────────────

const initialColumnState = {
	data: null,
	loading: false,
	error: null,
};

const initialState = {
	orders: { ...initialColumnState },
	assignments: { ...initialColumnState },
	payments: { ...initialColumnState },
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const dashboardSlice = createSlice({
	name: "dashboard",
	initialState,
	reducers: {
		resetDashboard: () => initialState,
	},
	extraReducers: (builder) => {
		// ── Orders ──────────────────────────────────────────────────────────────
		builder
			.addCase(loadOrderSummary.pending, (state) => {
				state.orders.loading = true;
				state.orders.error = null;
			})
			.addCase(loadOrderSummary.fulfilled, (state, action) => {
				state.orders.loading = false;
				state.orders.data = action.payload;
			})
			.addCase(loadOrderSummary.rejected, (state, action) => {
				state.orders.loading = false;
				state.orders.error = action.payload ?? "Unknown error";
			});

		// ── Assignments ──────────────────────────────────────────────────────────
		builder
			.addCase(loadAssignmentSummary.pending, (state) => {
				state.assignments.loading = true;
				state.assignments.error = null;
			})
			.addCase(loadAssignmentSummary.fulfilled, (state, action) => {
				state.assignments.loading = false;
				state.assignments.data = action.payload;
			})
			.addCase(loadAssignmentSummary.rejected, (state, action) => {
				state.assignments.loading = false;
				state.assignments.error = action.payload ?? "Unknown error";
			});

		// ── Payments ─────────────────────────────────────────────────────────────
		builder
			.addCase(loadPaymentSummary.pending, (state) => {
				state.payments.loading = true;
				state.payments.error = null;
			})
			.addCase(loadPaymentSummary.fulfilled, (state, action) => {
				state.payments.loading = false;
				state.payments.data = action.payload;
			})
			.addCase(loadPaymentSummary.rejected, (state, action) => {
				state.payments.loading = false;
				state.payments.error = action.payload ?? "Unknown error";
			});
	},
});

export const { resetDashboard } = dashboardSlice.actions;
export default dashboardSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectOrderSummary = (state) => state.dashboard.orders;
export const selectAssignmentSummary = (state) => state.dashboard.assignments;
export const selectPaymentSummary = (state) => state.dashboard.payments;
